<template>
  <div ref="rootRef" class="ui-search-dropdown" :class="shellClasses">
    <label v-if="label" :for="inputId" class="ui-search-dropdown__label">
      <span v-if="required" class="ui-search-dropdown__required" aria-hidden="true">*</span>{{ label }}
    </label>

    <div class="ui-search-dropdown__control">
      <input :id="inputId" ref="inputRef" :value="query" type="text" class="ui-input ui-search-dropdown__input"
        :class="[`ui-input--${variant}`]" :placeholder="placeholder" :disabled="disabled"
        :required="required" :aria-label="resolvedAriaLabel" :aria-expanded="isOpen ? 'true' : 'false'"
        aria-autocomplete="list" :aria-controls="menuId" role="combobox" autocomplete="off" @focus="openMenu"
        @input="handleInput" @keydown.down.prevent="moveSelection(1)" @keydown.up.prevent="moveSelection(-1)"
        @keydown.enter.prevent="commitActiveSelection" @keydown.esc.prevent="closeMenu">

      <Teleport to="body">
        <div v-if="isOpen" :id="menuId" ref="menuRef" class="ui-search-dropdown__menu ui-search-dropdown__menu--portal"
          :style="menuStyle" role="listbox">
          <div v-if="loading" class="ui-search-dropdown__empty">Loading results...</div>
          <div v-else-if="minChars > 0 && normalizedQuery.length < minChars" class="ui-search-dropdown__empty">
            Type at least {{ minChars }} characters.
          </div>
          <div v-else-if="results.length === 0" class="ui-search-dropdown__empty">No matching results found.</div>
          <button v-for="(item, index) in results" v-else :key="itemKey(item, index)" type="button" role="option"
            class="ui-search-dropdown__option" :class="{ 'is-active': activeIndex === index }"
            :aria-selected="isSelected(item) ? 'true' : 'false'" @pointerdown.prevent="selectItem(item)"
            @mouseenter="activeIndex = index">
            <span class="ui-search-dropdown__title">{{ resultTitle(item) }}</span>
            <span v-if="resultSubtitle(item)" class="ui-search-dropdown__subtitle">{{ resultSubtitle(item) }}</span>
          </button>
        </div>
      </Teleport>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import { onDocumentModalEvent } from '@/components/modal-events';
import { computeDropdownPosition, type DropdownPositionStyle } from '@/components/ui/dropdown-position';
import {
  filterSearchDropdownItems,
  findSearchDropdownItem,
  formatSearchDropdownValue,
  readSearchPathText,
  type SearchDropdownOutputMode,
  type SearchDropdownVariant
} from './SearchDropdown';

type SearchDropdownItem = Record<string, unknown>;

const props = withDefaults(defineProps<{
  modelValue?: string;
  items?: SearchDropdownItem[];
  valuePath?: string;
  subtitlePath?: string;
  searchKeys?: string[];
  getValue?: (item: SearchDropdownItem) => string;
  getSubtitle?: (item: SearchDropdownItem) => string;
  getSearchTexts?: (item: SearchDropdownItem) => string[];
  getItemKey?: (item: SearchDropdownItem, index: number) => string;
  label?: string;
  placeholder?: string;
  ariaLabel?: string;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
  minChars?: number;
  maxResults?: number;
  outputMode?: SearchDropdownOutputMode;
  valueSeparator?: string;
  variant?: SearchDropdownVariant;
}>(), {
  modelValue: '',
  items: () => [],
  valuePath: 'value',
  subtitlePath: 'subtitle',
  searchKeys: undefined,
  getValue: undefined,
  getSubtitle: undefined,
  getSearchTexts: undefined,
  getItemKey: undefined,
  label: '',
  placeholder: '',
  ariaLabel: '',
  required: false,
  disabled: false,
  loading: false,
  minChars: 2,
  maxResults: 8,
  outputMode: 'value',
  valueSeparator: ': ',
  variant: 'form'
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
  select: [item: SearchDropdownItem];
}>();

const rootRef = ref<HTMLElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);
const menuRef = ref<HTMLElement | null>(null);
const menuStyle = ref<DropdownPositionStyle>({});
const query = ref('');
const isOpen = ref(false);
const activeIndex = ref(-1);
const instanceId = `ui-search-dropdown-${Math.random().toString(36).slice(2, 9)}`;
const inputId = `${instanceId}-input`;
const menuId = `${instanceId}-menu`;
let removeModalHideListener: (() => void) | null = null;

const shellClasses = computed(() => [
  `ui-search-dropdown--${props.variant}`,
  {
    'is-open': isOpen.value,
    'is-disabled': props.disabled
  }
]);

const resolvedAriaLabel = computed(() => props.ariaLabel || props.label || 'Search');
const normalizedQuery = computed(() => query.value.toLowerCase().trim());

const results = computed<SearchDropdownItem[]>(() => filterSearchDropdownItems<SearchDropdownItem>({
  items: props.items,
  query: query.value,
  valuePath: props.valuePath,
  subtitlePath: props.subtitlePath,
  searchKeys: props.searchKeys,
  minChars: props.minChars,
  maxResults: props.maxResults,
  getValue: props.getValue,
  getSubtitle: props.getSubtitle,
  getSearchTexts: props.getSearchTexts
}));

function formatConfig() {
  return {
    valuePath: props.valuePath,
    subtitlePath: props.subtitlePath,
    getValue: props.getValue,
    getSubtitle: props.getSubtitle,
    valueSeparator: props.valueSeparator
  };
}

function displayValueFor(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';

  const matchedItem = findSearchDropdownItem(props.items, trimmed, {
    valuePath: props.valuePath,
    getValue: props.getValue
  });
  if (!matchedItem) return trimmed;

  return formatSearchDropdownValue(matchedItem, props.outputMode, formatConfig());
}

function resultTitle(item: SearchDropdownItem): string {
  if (props.getValue) return props.getValue(item);
  return readSearchPathText(item, props.valuePath);
}

function resultSubtitle(item: SearchDropdownItem): string {
  if (props.getSubtitle) return props.getSubtitle(item);
  if (!props.subtitlePath) return '';
  return readSearchPathText(item, props.subtitlePath);
}

function itemKey(item: SearchDropdownItem, index: number): string {
  if (props.getItemKey) return props.getItemKey(item, index);
  return `${resultTitle(item)}-${index}`;
}

function isSelected(item: SearchDropdownItem): boolean {
  const value = props.getValue ? props.getValue(item) : readSearchPathText(item, props.valuePath);
  return value === props.modelValue;
}

function updateMenuPosition(): void {
  if (!inputRef.value || !menuRef.value) return;
  menuStyle.value = computeDropdownPosition({
    trigger: inputRef.value,
    panel: menuRef.value,
    placement: 'auto',
    offset: 4,
    matchTriggerWidth: true,
    zIndex: 2600
  });
}

function schedulePositionUpdate(): void {
  void nextTick(() => {
    updateMenuPosition();
    window.requestAnimationFrame(() => updateMenuPosition());
  });
}

function openMenu(): void {
  if (props.disabled) return;
  isOpen.value = true;
  activeIndex.value = results.value.length ? 0 : -1;
  schedulePositionUpdate();
}

function closeMenu(): void {
  isOpen.value = false;
  activeIndex.value = -1;
  menuStyle.value = {};
}

function handleInput(event: Event): void {
  const nextValue = (event.target as HTMLInputElement).value;
  query.value = nextValue;
  emit('update:modelValue', nextValue);
  isOpen.value = true;
  activeIndex.value = results.value.length ? 0 : -1;
  schedulePositionUpdate();
}

function moveSelection(direction: 1 | -1): void {
  if (!isOpen.value) {
    openMenu();
    return;
  }
  if (!results.value.length) return;
  activeIndex.value = (activeIndex.value + direction + results.value.length) % results.value.length;
}

function commitActiveSelection(): void {
  const item = results.value[activeIndex.value];
  if (item) selectItem(item);
}

function selectItem(item: SearchDropdownItem): void {
  const nextValue = formatSearchDropdownValue(item, props.outputMode, formatConfig());
  query.value = nextValue;
  emit('update:modelValue', nextValue);
  emit('select', item);
  closeMenu();
}

function handleDocumentPointerDown(event: PointerEvent): void {
  const target = event.target as Node | null;
  if (!target) return;
  if (rootRef.value?.contains(target) || menuRef.value?.contains(target)) return;
  closeMenu();
}

function handleViewportChange(): void {
  if (isOpen.value) updateMenuPosition();
}

function focus(): void {
  inputRef.value?.focus();
}

watch(() => props.modelValue, value => {
  const nextDisplay = displayValueFor(value);
  if (nextDisplay !== query.value) query.value = nextDisplay;
}, { immediate: true });

watch(results, nextResults => {
  if (!nextResults.length) activeIndex.value = -1;
  else if (activeIndex.value < 0 || activeIndex.value >= nextResults.length) activeIndex.value = 0;
  if (isOpen.value) schedulePositionUpdate();
});

watch(() => props.disabled, disabled => {
  if (disabled) closeMenu();
});

watch(isOpen, open => {
  if (open) {
    window.addEventListener('resize', handleViewportChange, true);
    window.addEventListener('scroll', handleViewportChange, true);
    return;
  }
  window.removeEventListener('resize', handleViewportChange, true);
  window.removeEventListener('scroll', handleViewportChange, true);
});

onMounted(() => {
  document.addEventListener('pointerdown', handleDocumentPointerDown);
  removeModalHideListener = onDocumentModalEvent('hide.bs.modal', closeMenu);
});

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', handleDocumentPointerDown);
  removeModalHideListener?.();
  window.removeEventListener('resize', handleViewportChange, true);
  window.removeEventListener('scroll', handleViewportChange, true);
});

defineExpose({
  focus,
  closeMenu,
  inputRef
});
</script>
